import type { QuizQuestionSet, QuizFieldType } from "../quiz.types";
import { makeQuestionSetFormFields } from "./makeQuestionSetFormFields";

/**
 * A grouped set of form fields for a quiz question set.
 * Contains the header/description metadata plus the generated form fields.
 */
export type FieldGroup = {
  /** Header text for the group (from question set) */
  header: string;
  /** Optional description text for the group */
  description?: string;
  /** Form fields generated from the questions in this group */
  fields: QuizFieldType[];
};

/**
 * Groups form fields by their question set header.
 *
 * Takes an array of QuizQuestionSets (already grouped by the API/normalization)
 * and transforms them into FieldGroups with generated form fields.
 *
 * @param questionSets - Array of question sets from the quiz
 * @returns Array of field groups with header, description, and fields
 *
 * @example
 * ```tsx
 * const fieldGroups = groupFieldsByQuestionSet(quiz.questionSets);
 * fieldGroups.forEach(group => {
 *   console.log(group.header); // "Personal Info"
 *   console.log(group.fields); // [{ name: 'q_1', ... }, ...]
 * });
 * ```
 */
export function groupFieldsByQuestionSet(
  questionSets: QuizQuestionSet[]
): FieldGroup[] {
  return questionSets.map((set) => ({
    header: set.header,
    description: set.description,
    fields: makeQuestionSetFormFields(set.questions),
  }));
}
